import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type FocusEvent,
  type ReactNode,
} from "react";
import { cn } from "@/shared/lib/cn";
import { Eye, EyeOff } from "@/shared/components/icons";
import { messages } from "@/shared/i18n";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Optional node rendered on the right side of the label row (e.g. a "Forgot password?" link) */
  labelRight?: ReactNode;
  error?: string | undefined;
  helperText?: string;
  fullWidth?: boolean;
  "data-testid"?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      labelRight,
      error,
      helperText,
      fullWidth = true,
      className,
      id,
      type,
      "data-testid": dataTestId,
      ...props
    },
    ref
  ) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [capsLockOn, setCapsLockOn] = useState(false);
    const isPasswordField = type === "password";
    const effectiveType = isPasswordField ? (isPasswordVisible ? "text" : "password") : type;

    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = `${inputId}-error`;
    const capsLockId = `${inputId}-capslock`;
    const hasError = Boolean(error);
    const showCapsLock = isPasswordField && capsLockOn;

    // Tie any visible hints to the input for screen readers.
    const describedBy =
      [hasError ? errorId : null, showCapsLock ? capsLockId : null].filter(Boolean).join(" ") ||
      undefined;

    const handleTogglePasswordVisibility = () => {
      setIsPasswordVisible((prev) => !prev);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      props.onKeyDown?.(event);
      setCapsLockOn(event.getModifierState("CapsLock"));
    };

    const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
      props.onKeyUp?.(event);
      setCapsLockOn(event.getModifierState("CapsLock"));
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      props.onBlur?.(event);
      setCapsLockOn(false);
    };

    return (
      <div className={cn(fullWidth && "w-full")}>
        {(label ?? labelRight) && (
          <div className="mb-1 flex items-center justify-between">
            <label className="text-base text-gray-700" htmlFor={inputId}>
              {label}
              {props.required && (
                <span aria-label="required" className="ml-0.5 text-red-600">
                  *
                </span>
              )}
            </label>
            {labelRight && <span>{labelRight}</span>}
          </div>
        )}
        <div className="relative">
          <input
            ref={ref}
            aria-describedby={describedBy}
            aria-invalid={hasError}
            className={cn(
              "control-md block w-full rounded-lg border transition-colors",
              "bg-white text-gray-900 placeholder:text-gray-400",
              "focus:ring-2 focus:outline-none",
              hasError
                ? "border-red-400 focus:border-red-400 focus:ring-red-400/20"
                : "focus:border-brand-primary focus:ring-brand-primary/20 border-gray-300",
              "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
              isPasswordField && "pr-10",
              className
            )}
            data-testid={dataTestId}
            id={inputId}
            type={effectiveType}
            {...props}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
          />
          {isPasswordField && (
            <button
              aria-label={
                isPasswordVisible
                  ? messages.components.accessibility.hidePassword
                  : messages.components.accessibility.showPassword
              }
              className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1.5 text-gray-400 transition-colors hover:text-gray-600 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
              data-testid={dataTestId ? `${dataTestId}-toggle-password` : undefined}
              type="button"
              onClick={handleTogglePasswordVisibility}
            >
              {isPasswordVisible ? (
                <EyeOff aria-hidden className="h-5 w-5" />
              ) : (
                <Eye aria-hidden className="h-5 w-5" />
              )}
            </button>
          )}
        </div>
        {showCapsLock && (
          <p
            className="mt-1 text-sm text-yellow-600"
            data-testid={dataTestId ? `${dataTestId}-capslock` : undefined}
            id={capsLockId}
            role="status"
          >
            {messages.components.input.capsLockOn}
          </p>
        )}
        {(error ?? helperText) && (
          <p
            className={cn("mt-1 text-sm", hasError ? "text-red-600" : "text-gray-600")}
            data-testid={dataTestId ? `${dataTestId}-error` : undefined}
            id={errorId}
          >
            {error ?? helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";
